import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { ProgressSpinner } from "primereact/progressspinner";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import DualCurrencyValue from "../../components/money/DualCurrencyValue";
import { CASH_TRANSACTION_TYPES, fetchAdminRegisterSummary, fetchCashRegisters, formatCurrency } from "../../services/cashService";
import styles from "../shifts/shifts.module.scss";

const RANGE_OPTIONS = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "day" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
];

const staffName = (staff) => (staff ? `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || staff.email : "Unknown");

// Register-level float figures (starting/closing cash, discrepancy, current
// balance) are always a { usd, jmd } pair now - a till can hold both at
// once, so these never collapse into one blended number. Both sides are
// always shown, even at zero - an admin needs to see "no JMD activity" as
// an explicit fact, not have it silently disappear.
const formatPair = (p) => `${formatCurrency(p?.usd || 0, "usd")} + ${formatCurrency(p?.jmd || 0, "jmd")}`;
const emptyPair = () => ({ usd: 0, jmd: 0 });
const addPair = (a, b) => ({ usd: (a?.usd || 0) + (b?.usd || 0), jmd: (a?.jmd || 0) + (b?.jmd || 0) });

// Turns one story timeline item (a cash entry or a register acquire/release
// event) into a single narrative line, so the day/week view reads like a
// log of what actually happened rather than a raw transaction table.
const storyItemLine = (item) => {
    const time = moment(item.at).format("h:mm A");
    const who = staffName(item.staff);
    if (item.kind === "register") {
        if (item.action === "acquired") {
            return { icon: "pi pi-lock-open", text: `${who} acquired the register with ${formatPair(item.amount)} starting cash`, time };
        }
        const hasDiscrepancy = !!((item.discrepancy?.usd && Math.abs(item.discrepancy.usd) > 0.001) || (item.discrepancy?.jmd && Math.abs(item.discrepancy.jmd) > 0.001));
        return {
            icon: "pi pi-lock",
            text: `${who} released the register with ${formatPair(item.amount)} counted (expected ${formatPair(item.expectedAmount)}${
                hasDiscrepancy ? `, discrepancy ${formatPair(item.discrepancy)}` : ""
            })`,
            time,
            flagged: hasDiscrepancy,
        };
    }
    const attribution = item.recordedByOtherHolder ? ` - recorded by ${who} while ${item.registerHolderName} held the register` : ` - ${who}`;
    return {
        icon: item.type === "out" ? "pi pi-arrow-up" : "pi pi-arrow-down",
        text: `${item.type === "out" ? "-" : "+"}${formatCurrency(item.amount, item.currency)} Cash ${item.type === "out" ? "Out" : "In"}${item.reason ? ` - ${item.reason}` : ""}${attribution}`,
        time,
        flagged: item.recordedByOtherHolder,
    };
};

const StoryDay = ({ day }) => (
    <div className={styles.storyDay}>
        <div className={styles.storyDayHeader}>
            <span className={styles.storyDayLabel}>{day.label}</span>
            <span className={styles.storyDayTotals}>
                <span className={styles.storyIn}>+{formatPair(day.cashIn)}</span>
                <span className={styles.storyOut}>-{formatPair(day.cashOut)}</span>
                <span className={styles.storyNet}>Net {formatPair(day.net)}</span>
            </span>
        </div>
        {day.items.length === 0 ? (
            <div className={styles.emptyBreaks}>No activity.</div>
        ) : (
            <ul className={styles.storyItems}>
                {day.items.map((item, idx) => {
                    const line = storyItemLine(item);
                    return (
                        <li key={idx} className={`${styles.storyItem} ${line.flagged ? styles.storyItemFlagged : ""}`}>
                            <i className={line.icon} />
                            <span className={styles.storyItemText}>{line.text}</span>
                            <span className={styles.storyItemTime}>{line.time}</span>
                        </li>
                    );
                })}
            </ul>
        )}
    </div>
);

const StoryView = ({ story }) => {
    if (!story) return null;
    if (story.granularity === "day") {
        return (
            <div className={styles.storyList}>
                {story.groups.map((day) => (
                    <StoryDay key={day.dayKey} day={day} />
                ))}
            </div>
        );
    }
    return (
        <div className={styles.storyList}>
            {story.groups.map((week) => (
                <div key={week.weekKey} className={styles.storyWeek}>
                    <div className={styles.storyWeekHeader}>
                        <span className={styles.storyWeekLabel}>{week.label}</span>
                        <span className={styles.storyDayTotals}>
                            <span className={styles.storyIn}>+{formatPair(week.cashIn)}</span>
                            <span className={styles.storyOut}>-{formatPair(week.cashOut)}</span>
                            <span className={styles.storyNet}>Net {formatPair(week.net)}</span>
                        </span>
                    </div>
                    {week.days.map((day) => (
                        <StoryDay key={day.dayKey} day={day} />
                    ))}
                </div>
            ))}
        </div>
    );
};

const AdminCashLedger = () => {
    const [range, setRange] = useState("all");
    const [dateRange, setDateRange] = useState(null);
    const [type, setType] = useState(null);

    const [loading, setLoading] = useState(true);
    const [registers, setRegisters] = useState([]);
    const [registerSummaries, setRegisterSummaries] = useState([]);
    const [selectedRegisterId, setSelectedRegisterId] = useState(null);

    const dateFrom = dateRange && dateRange.length === 2 && dateRange[1] ? dateRange[0].toISOString() : null;
    const dateTo = dateRange && dateRange.length === 2 && dateRange[1] ? dateRange[1].toISOString() : null;

    const loadData = async () => {
        setLoading(true);
        const regRes = await fetchCashRegisters();
        const regs = regRes?.data || [];
        setRegisters(regs);

        const summaries = await Promise.all(regs.map((r) => fetchAdminRegisterSummary(r._id, dateFrom && dateTo ? null : range, dateFrom, dateTo)));
        setRegisterSummaries(
            summaries.map((res, i) => ({
                register: regs[i],
                totals: res?.data?.totals || { cashIn: emptyPair(), cashOut: emptyPair(), net: emptyPair() },
                currentBalance: res?.data?.currentBalance || emptyPair(),
                currentHolder: res?.data?.currentHolder || null,
                entries: res?.data?.entries || [],
                story: res?.data?.story || null,
            })),
        );

        // Only fills in a default the first time - once an admin has picked a
        // register, changing the date range shouldn't silently switch it back.
        setSelectedRegisterId((prev) => (prev && regs.some((r) => r._id === prev) ? prev : regs[0]?._id || null));
        setLoading(false);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, dateRange]);

    const aggregateTotals = useMemo(
        () =>
            registerSummaries.reduce(
                (acc, s) => ({
                    cashIn: addPair(acc.cashIn, s.totals.cashIn),
                    cashOut: addPair(acc.cashOut, s.totals.cashOut),
                    net: addPair(acc.net, s.totals.net),
                    currentBalance: addPair(acc.currentBalance, s.currentBalance),
                }),
                { cashIn: emptyPair(), cashOut: emptyPair(), net: emptyPair(), currentBalance: emptyPair() },
            ),
        [registerSummaries],
    );

    const selectedSummary = registerSummaries.find((s) => s.register._id === selectedRegisterId);
    const selectedEntries = useMemo(() => {
        const entries = selectedSummary?.entries || [];
        return type ? entries.filter((e) => e.type === type) : entries;
    }, [selectedSummary, type]);

    // Register acquire/release events are always kept (they're not "cash in"
    // or "cash out"), only the cash items get narrowed by the Type filter.
    const selectedStory = useMemo(() => {
        const story = selectedSummary?.story;
        if (!story) return null;
        if (!type) return story;
        const filterItems = (items) => items.filter((item) => item.kind === "register" || item.type === type);
        if (story.granularity === "day") {
            return { ...story, groups: story.groups.map((d) => ({ ...d, items: filterItems(d.items) })) };
        }
        return { ...story, groups: story.groups.map((w) => ({ ...w, days: w.days.map((d) => ({ ...d, items: filterItems(d.items) })) })) };
    }, [selectedSummary, type]);

    const dateBody = (row) => (row.createdAt ? new Date(row.createdAt).toLocaleString() : "-");
    const staffBody = (row) => (
        <>
            {staffName(row.staff)}
            {row.recordedByOtherHolder && (
                <span className={styles.otherHolderTag} title={`This register was held by ${row.registerHolderName} at the time - ${staffName(row.staff)} recorded this entry instead.`}>
                    <i className="pi pi-users" /> Other Staff
                </span>
            )}
        </>
    );
    const typeBody = (row) => <span className={`${styles.badge} ${row.type === "out" ? styles.badgePending : styles.badgeActive}`}>{row.type === "out" ? "Cash Out" : "Cash In"}</span>;
    const amountBody = (row) => `${row.type === "out" ? "-" : "+"}${formatCurrency(row.amount, row.currency)}`;
    const invoiceBody = (row) => row.invoiceId?.invoiceId || row.invoiceId?.Waybills || "-";

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-book" />
                    </div>
                    <div>
                        <h2>Cash Ledger</h2>
                        <p className={styles.subtitle}>Register-by-register cash balances and entries</p>
                    </div>
                </div>
            </div>

            <div className={styles.filtersSection}>
                <div className={styles.filterGroup}>
                    <label>Quick Range</label>
                    <div className={styles.quickRangeRow}>
                        {RANGE_OPTIONS.map((opt) => (
                            <Button
                                key={opt.value}
                                label={opt.label}
                                className={`${styles.quickRangeBtn} ${range === opt.value && !dateRange ? styles.activeRange : ""}`}
                                onClick={() => {
                                    setDateRange(null);
                                    setRange(opt.value);
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="dateRange">Custom Range</label>
                    <Calendar id="dateRange" value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput placeholder="Select date range" />
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="type">Type</label>
                    <Dropdown id="type" value={type} options={CASH_TRANSACTION_TYPES} onChange={(e) => setType(e.value)} placeholder="All Types" showClear style={{ minWidth: "160px" }} />
                </div>
                <div className={styles.filterGroup}>
                    <Button label="Apply" icon="pi pi-search" onClick={loadData} className={styles.primaryButton} />
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading cash ledger...</span>
                </div>
            ) : registers.length === 0 ? (
                <div className={styles.sectionCard}>
                    <div className={styles.emptyBreaks}>No cash registers yet. Create one from Cash Registers.</div>
                </div>
            ) : (
                <>
                    <div className={styles.statsRow}>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-down" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={aggregateTotals.cashIn} />
                                <div className={styles.statLabel}>Total Cash In</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-arrow-up" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={aggregateTotals.cashOut} />
                                <div className={styles.statLabel}>Total Cash Out</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-wallet" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={aggregateTotals.net} />
                                <div className={styles.statLabel}>Net Movement (Period)</div>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <i className="pi pi-lock" />
                            </div>
                            <div>
                                <DualCurrencyValue pair={aggregateTotals.currentBalance} />
                                <div className={styles.statLabel}>Total Register Balance (incl. starting cash)</div>
                            </div>
                        </div>
                        {registerSummaries.map((s) => (
                            <div className={styles.statCard} key={s.register._id}>
                                <div className={styles.statIcon}>
                                    <i className="pi pi-inbox" />
                                </div>
                                <div>
                                    <DualCurrencyValue pair={s.currentBalance} />
                                    <div className={styles.statLabel}>{s.register.name}{s.currentHolder ? ` - ${s.currentHolder}` : ""}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.filterGroup} style={{ marginBottom: "10px" }}>
                        <label>Select Register</label>
                    </div>
                    <div className={styles.registerPicker}>
                        {registerSummaries.map((s) => (
                            <button
                                key={s.register._id}
                                type="button"
                                className={`${styles.registerPickerItem} ${s.register._id === selectedRegisterId ? styles.registerPickerActive : ""}`}
                                onClick={() => setSelectedRegisterId(s.register._id)}
                            >
                                <span className={styles.registerPickerName}>{s.register.name}</span>
                                <span className={styles.registerPickerBalance} style={{ fontSize: "13px" }}>{formatPair(s.currentBalance)}</span>
                            </button>
                        ))}
                    </div>

                    <TabView>
                        <TabPanel header="Transactions" leftIcon="pi pi-table">
                            <ResponsiveView
                                value={selectedEntries}
                                emptyMessage="No cash entries found for this register."
                                storageKey="adminCashLedger"
                                renderCard={(row) => (
                                    <DataCard
                                        key={row._id || row.createdAt}
                                        accent={row.type === "out" ? "amber" : "green"}
                                        title={amountBody(row)}
                                        subtitle={staffBody(row)}
                                        badge={typeBody(row)}
                                        fields={[
                                            { label: "Date", value: dateBody(row) },
                                            { label: "Reason", value: row.reason },
                                            { label: "Invoice", value: invoiceBody(row) },
                                        ]}
                                    />
                                )}
                            >
                                <DataTable
                                    value={selectedEntries}
                                    paginator
                                    rows={20}
                                    rowsPerPageOptions={[20, 50, 100]}
                                    className="datatable-responsive custom-datatable"
                                    emptyMessage="No cash entries found for this register."
                                    rowHover
                                    rowClassName={(row) => (row.recordedByOtherHolder ? styles.otherHolderRow : "")}
                                >
                                    <Column header="Date" body={dateBody} />
                                    <Column header="Staff" body={staffBody} />
                                    <Column header="Type" body={typeBody} />
                                    <Column header="Amount" body={amountBody} />
                                    <Column field="reason" header="Reason" />
                                    <Column header="Linked Invoice" body={invoiceBody} />
                                </DataTable>
                            </ResponsiveView>
                        </TabPanel>
                        <TabPanel header="Timeline" leftIcon="pi pi-book">
                            {selectedStory ? (
                                <StoryView story={selectedStory} />
                            ) : (
                                <div className={styles.emptyBreaks}>No activity found for this register.</div>
                            )}
                        </TabPanel>
                    </TabView>
                </>
            )}
        </div>
    );
};

export default AdminCashLedger;
